import React, { useEffect, useRef, useState } from 'react';
import { resolveInitialPayerPhone } from '../utils/deliveryPhone';
import {
    formatCurrency,
    isFwMomoMethod,
    fwNetworkFor,
} from "../config/market";
import useMinimumOrderValue from "../hooks/useMinimumOrderValue";
import { linesSubtotal, meetsMinimumOrder } from "../utils/minimumOrder";
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import CircularProgress from '@material-ui/core/CircularProgress';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import useDeliveryFee from '../redux/useDeliveryFee';
import DeliveryFeeStatus from '../components/DeliveryFeeStatus';
import FreightCheckoutNotice from '../components/FreightCheckoutNotice';
import RetailerWholesalerNavbar from '../components/RetailerWholesalerNavbar';
import CheckoutStepper from '../components/CheckoutStepper';
import { createOrder, payAirtelMoneyCheckout, payCardCheckout, payMoMoCheckout } from '../redux/retailerWholesalerOrderActions';
import CreditCardForm from '../components/CreditCardForm/CreditCardForm';
import airtel from '../assets/img/airtel_money.png';
import momo from '../assets/img/momo.jpg';
import { clearCart } from '../redux/retailerWholesalerCartSlice';
import { newIdempotencyKey } from '../utils/idempotency';
import { extractCreatedOrderId } from "../redux/checkout";
import { followCardRedirect } from "../utils/cardPayment";
import { apiSetContactEmail } from "../api/profileApi";
import { toast } from "react-toastify";
import { StruckPrice, VendorOfferAmount } from "../components/PriceCut";
import { lineHasDeal, cartOfferSavings } from "../utils/vendorDiscounts";
import useCheckoutDeliverySchedule from "../hooks/useCheckoutDeliverySchedule";
import DeliveryTimePicker from "../components/DeliveryTimePicker";

// ─────────────────────────────────────────────────────────────────────────────
// Rams-optimized Confirm page — same shell as RestaurantPlaceOrderScreen:
// narrow centered column instead of a full-viewport table; "Deliver to" at the
// top; borderless item rows; sticky cost summary where the TOTAL is the
// loudest element. PRESENTATION ONLY — every handler, fee gate
// (deliveryFeeReady), DeliveryFeeStatus usage and disabled condition is
// unchanged (Order-Placement Contract §B/§C). One presentational bug fixed:
// the old table's "Sum" column rendered the CART total on every row; rows now
// show their own line total (price × quantity).
// ─────────────────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
    input: {
        "& input[type=number]": {
            "-moz-appearance": "textfield",
        },
        "& input[type=number]::-webkit-outer-spin-button": {
            "-webkit-appearance": "none",
            margin: 0,
            paddingBottom: "5px",
        },
        "& input[type=number]::-webkit-inner-spin-button": {
            "-webkit-appearance": "none",
            margin: 0,
        },
    },
    inputGreen: {
        "& .MuiOutlinedInput-root": {
            "& fieldset": {
                borderColor: "green",
            },
        },
        "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            borderColor: "green",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "green",
        },
        "& .MuiOutlinedInput-input": {
            color: "green",
        },
        "&:hover .MuiOutlinedInput-input": {
            color: "green",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input": {
            color: "green",
        },
    },
}));

const StyledTextField = styled(TextField)`
  & .MuiInputBase-root {
    padding: 10px; /* Add padding to ensure proper alignment */
  }
  & .MuiOutlinedInput-input {
    padding: 12px; /* Adjust padding to align placeholder */
  }
`;

const validationSchema = Yup.object().shape({
    cardNumber: Yup.string().required('Card Number is required.'),
    expiry_month: Yup.string().required('Expiry Month is required.'),
    expiry_year: Yup.string().required('Expiry Year is required.'),
    cardCvv: Yup.string().required('CVV is required.'),
    cardType: Yup.string().required('Card Type is required.'),
    fullName: Yup.string().required('Full Name is required.'),
});

const RetailerWholesalerPlaceOrderScreen = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const classes = useStyles();

    const { shippingAddress, paymentMethod } = useSelector(
        (state) => state.retailerWholesalerCart
    );
    const currentVendor = useSelector(
        (state) => state.retailerWholesalerCart.currentVendor
    );

    const cart = useSelector(
        (state) => state.retailerWholesalerCart.carts[currentVendor] || {}
    );

    const products = Object.values(cart);

    /* THIS vendor's goods, and only this vendor's.
       `retailerWholesalerCart.total` is the sum of EVERY open retailer cart
       (retailerWholesalerCartSlice.js calculateTotal walks all of them), while
       this screen checks out ONE vendor: with two carts open it showed, and
       posted, the other vendor's goods too. The server re-prices the lines it
       was actually sent, so nobody was overcharged, but the figure on the
       review screen disagreed with what was about to be charged. */
    const goodsSubtotal = linesSubtotal(products);
    // What the vendor's live offers took off; the subtotal is already net of it.
    const offerSavings = cartOfferSavings(products);

    /* Second line of defence on the minimum order value. The cart says it first
       and the server is the gate that counts; this stops a deep link or a tab
       left open since before the floor existed from posting a basket the server
       will only refuse after the customer has entered a phone number. Summed
       from THIS vendor's lines: the slice's `total` is every vendor's cart at
       once, and each vendor ships as its own order. */
    const minimumOrderValue = useMinimumOrderValue();
    const belowMinimumOrder = !meetsMinimumOrder(
        goodsSubtotal,
        minimumOrderValue
    );

    const user = useSelector((state) => state.user.currentUser);

    const orderCreationStatus = useSelector(
        (state) => state.retailerWholesalerOrder.orderCreationStatus
    );
    const paymentCreationStatus = useSelector(
        (state) => state.retailerWholesalerOrder.paymentCreationStatus
    );

    const [orderSource] = useState('Web');

    useEffect(() => {
        if (!currentVendor) {
            history.push('/');
        } else if (
            Object.keys(shippingAddress).length === 0 ||
            Object.keys(paymentMethod).length === 0 ||
            Object.keys(products).length === 0
        ) {
            history.push(`/retailer-wholesaler/${currentVendor}`);
        }
    }, [currentVendor, shippingAddress, paymentMethod, products, history]);

    // Server-authoritative delivery fee (distance-based, signed quote).
    const {
        fee: serverShippingFee,
        distanceKm,
        quoteToken,
        isReady: deliveryFeeReady,
        isFailed: deliveryFeeFailed,
        isBlocked: deliveryFeeBlocked,
        error: deliveryFeeError,
        refresh: refreshDeliveryFee,
        fulfillment: quoteFulfillment,
        freightConsentRequired,
    } = useDeliveryFee({
        vendorId: currentVendor,
        customerLat: shippingAddress?.latLng?.lat,
        customerLng: shippingAddress?.latLng?.lng,
        items: products,
    });

    // Express or a scheduled window from this shop's published hours. A shop
    // that is closed now but takes scheduled orders reports it through the
    // slot list, which then forces Scheduled.
    const deliveryTime = useCheckoutDeliverySchedule({
        flow: 'rw',
        vendorId: currentVendor,
    });
    // Shared props for the fee/total cells (Contract §C3): failures are VISIBLE
    // with a Retry, never a permanent 'Calculating…'.
    const feeStatusProps = {
        isReady: deliveryFeeReady,
        isFailed: deliveryFeeFailed,
        isBlocked: deliveryFeeBlocked,
        error: deliveryFeeError,
        onRetry: refreshDeliveryFee,
    };
    // Display-only total — the server recomputes the charged total on order-create.
    const totalOrderCostPlusShipping = Number(goodsSubtotal) + Number(serverShippingFee);

    // One idempotency key per checkout attempt: retries of THIS order reuse it
    // so the backend dedupes instead of double-creating (Contract §B4 — the
    // restaurant confirm screen already does this; RW previously did not).
    const idempotencyKeyRef = useRef(newIdempotencyKey());

    useEffect(() => {
        // REPLACE (not push) so this confirm screen falls out of the back
        // stack — Back from the success page must never land on a
        // re-submittable checkout step (double-order protection; pairs with
        // the success page's useBlockBackNavigation guard).
        if (paymentMethod === 'MOMO' && !isFwMomoMethod('MOMO') && orderCreationStatus === 'fulfilled') {
            // Direct-MTN status page is Uganda-only; FW-momo markets redirect to Flutterwave.
            history.replace('/momoOrderPage');
        } else if (paymentMethod === 'COD' && orderCreationStatus === 'fulfilled') {
            history.replace('/rworderPage');
        }
    }, [orderCreationStatus, paymentMethod, history]);

    const formik = useFormik({
        initialValues: {
            cardNumber: '',
            expiry_month: '',
            expiry_year: '',
            cardCvv: '',
            cardType: '',
            fullName: '',
        },
        validationSchema,
        onSubmit: (values) => {
            cardPlaceOrderHandler(values);
        },
    });

    const [postData, setPostData] = useState({
        mobile: '',
    });    
    // Payer prefill: most customers pay with the number we deliver to (or their
    // account number), so seed the mobile-money field once instead of forcing a
    // retype. Fully editable for "someone else is paying", and the once-guard
    // means we never fight a customer who clears the field on purpose.
    const payerPrefilledRef = useRef(false);
    useEffect(() => {
      if (payerPrefilledRef.current) return;
      const prefill = resolveInitialPayerPhone(shippingAddress, user);
      if (prefill) {
        payerPrefilledRef.current = true;
        setPostData((d) => (d.mobile ? d : { ...d, mobile: prefill }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shippingAddress, user]);


    const userId = user?.result?._id || user?._id;
    const userName = user?.result?.name || user?.name;
    const userEmail = user?.result?.email || user?.email;
    // Optional email capture. Most Lusuku customers register by PHONE and have
    // no email, so the receipt has nowhere to go. We ask here; never required.
    const [emailInput, setEmailInput] = useState('');
    const needsEmail = !userEmail;
    useEffect(() => {
        if (!userId) {
            history.push('/login');
        }
    }, [userId, history]);

    const placeOrderHandler = (e) => {
        e.preventDefault();
        deliveryTime.submitOrder(createOrder, {
            orderItems: products?.map((item) => ({
                ...item,
                // vendorId: item.vendorId,
                vendorId: currentVendor,
            })),
            shippingAddress,
            // Server recomputes the fee/total; quote token lets it honour
            // the displayed fee. No client-computed fee is authoritative.
            clientQuoteToken: quoteToken,
            distanceKm,
            paymentMethod,
            itemsPrice: goodsSubtotal,
            totalPrice: totalOrderCostPlusShipping,
            customerName: userName,
            client_id: userId,
            orderSrc: orderSource,
            vendorId: currentVendor,
            qty: products?.map((item) => item.quantity),
            image: products?.map((item) => item.image),
            idempotencyKey: idempotencyKeyRef.current,
        }).then((action) => {
            // Only clear the cart once the order is actually persisted. On a
            // rejected order (e.g. 422 OUT_OF_DELIVERY_RANGE) keep the cart so
            // the customer can fix their address and retry. Navigation to the
            // success page is gated on orderCreationStatus === 'fulfilled'.
            if (!action.error && action.meta?.requestStatus === 'fulfilled') {
                dispatch(clearCart(currentVendor));
            }
        });
    };

    const payMoMoCheckoutHandler = async (e) => {
        e.preventDefault();

        // MARKET RAIL SWITCH: ZM "MOMO" charges over the FW rail (network MTN).
        if (isFwMomoMethod('MOMO')) {
            return payAirtelMoneyCheckoutHandler(e, fwNetworkFor('MOMO'));
        }

        // WHY save but not send: no MoMo charge payload in this codebase carries
        // an email, so we only persist it to the profile — that is where the
        // order confirmation is addressed from. Best-effort; never blocks pay.
        const typedEmail = emailInput.trim();
        if (!userEmail && typedEmail) {
            apiSetContactEmail(typedEmail).catch(() => {});
        }

        const orderItems = products?.map((item) => ({
            name: item.name,
            // RW cart items have no retailerWholesalerId; the active vendor is
            // the canonical vendorId (mirrors what COD sends). Without this the
            // order has no vendor and the create-order coverage gate can't run.
            vendorId: currentVendor,
            qty: item.quantity,
            price: item.price,
            image: item.image,
        }));

        // Create the order FIRST and await it. Previously payment and
        // createOrder fired concurrently/un-awaited, so a customer could be
        // charged with no order (or with an order that the coverage gate later
        // rejects). Now we only initiate payment after the order is persisted.
        const orderAction = await deliveryTime.submitOrder(createOrder, {
            orderItems,
            shippingAddress,
            // Server recomputes the fee/total; quote token lets it honour
            // the displayed fee. No client-computed fee is authoritative.
            clientQuoteToken: quoteToken,
            distanceKm,
            paymentMethod,
            itemsPrice: goodsSubtotal,
            totalPrice: totalOrderCostPlusShipping,
            customerName: userName,
            client_id: userId,
            orderSrc: orderSource,
            vendorId: currentVendor,
            idempotencyKey: idempotencyKeyRef.current,
        });

        // Stop the flow if the order failed (rejected / 4xx / 422). The thunk
        // already surfaced the backend message (e.g. OUT_OF_DELIVERY_RANGE)
        // via toast, so just bail without charging the customer.
        if (orderAction.error || orderAction.meta?.requestStatus !== 'fulfilled') {
            return;
        }

        const orderId = extractCreatedOrderId(orderAction);

        dispatch(
            payMoMoCheckout({
                mobile: postData.mobile,
                totalPrice: totalOrderCostPlusShipping,
                client_id: userId,
                orderItems,
                // Bind the payment to the order we just created instead of the
                // customer's "latest order". Backward-compatible: the backend
                // ignores orderId until updated to read it.
                orderId,
            })
        );
    };

    const generateTxRef = (clientId) => {
        return clientId + '-' + Date.now();
    };

    const payAirtelMoneyCheckoutHandler = async (e, networkOverride) => {
        e.preventDefault();
        const orderItems = products?.map((item) => ({
            name: item.name,
            // RW cart items have no retailerWholesalerId; use the active vendor
            // (mirrors COD) so the order is attributed and the coverage gate runs.
            vendorId: currentVendor,
            qty: item.quantity,
            price: item.price,
            image: item.image,
        }));

        // Create + await the order before charging (see payMoMoCheckoutHandler).
        const orderAction = await deliveryTime.submitOrder(createOrder, {
            orderItems,
            shippingAddress,
            // Server recomputes the fee/total; quote token lets it honour
            // the displayed fee. No client-computed fee is authoritative.
            clientQuoteToken: quoteToken,
            distanceKm,
            paymentMethod,
            itemsPrice: goodsSubtotal,
            totalPrice: totalOrderCostPlusShipping,
            customerName: userName,
            client_id: userId,
            orderSrc: orderSource,
            vendorId: currentVendor,
            idempotencyKey: idempotencyKeyRef.current,
        });

        if (orderAction.error || orderAction.meta?.requestStatus !== 'fulfilled') {
            return;
        }

        const orderId = extractCreatedOrderId(orderAction);
        // Embed the order id in tx_ref as `${clientId}-${orderId}` so the
        // Flutterwave webhook can bind payment to THIS order (the backend
        // splits tx_ref on "-"). Falls back to a timestamp if id is missing.
        const uniqueTxRef = orderId ? `${userId}-${orderId}` : generateTxRef(userId);

        // Use whatever they typed for THIS charge and save it to the profile too.
        // Best-effort: a failed save must never stop the payment.
        const typed = emailInput.trim();
        const effectiveEmail = userEmail || typed || undefined;
        if (!userEmail && typed) {
            apiSetContactEmail(typed).catch(() => {});
        }

        dispatch(
            payAirtelMoneyCheckout({
                mobile: postData.mobile,
                totalPrice: totalOrderCostPlusShipping,
                client_id: userId,
                email: effectiveEmail,
                uniqueTxRef,
                network: networkOverride || fwNetworkFor(paymentMethod) || paymentMethod,
                redirect_url: `${window.location.origin}/airtelPay`,
                orderItems,
                orderId,
            })
        ).then((action) => {
            if (!action.error && action.payload?.data?.meta?.authorization) {
                followCardRedirect(action.payload.redirectUrl);
            } else if (!action.error) {
                // The order already EXISTS: createOrder ran and was awaited above, so
                // silence here leaves the customer holding an unpaid order with no next
                // step and no idea anything went wrong. A REJECTED thunk has already
                // toasted the backend's own reason, so re-toasting it would just double
                // up; a 200 whose body carries no `meta.authorization` had no branch at
                // all, and that is the case this else was missing.
                toast.error(
                    "We couldn't start the Airtel Money payment. Your order was created but has NOT been paid \u2014 open it in My Orders to try again.",
                    { autoClose: false }
                );
            }
        });
    };

    const cardPlaceOrderHandler = async () => {
        const cardDetails = {
            cardNumber: formik.values.cardNumber,
            expiry_month: formik.values.expiry_month,
            expiry_year: formik.values.expiry_year,
            cardCvv: formik.values.cardCvv,
            cardType: formik.values.cardType,
            fullName: formik.values.fullName,
        };

        // Card previously sent raw `products` with no vendorId. Normalise line
        // items and stamp the active vendor so the order is attributed and the
        // coverage gate runs (mirrors COD / the other payment methods).
        const orderItems = products?.map((item) => ({
            ...item,
            vendorId: currentVendor,
        }));

        // Create + await the order before charging the card (see MoMo handler).
        const orderAction = await deliveryTime.submitOrder(createOrder, {
            orderItems,
            shippingAddress,
            // Server recomputes the fee/total; quote token lets it honour
            // the displayed fee. No client-computed fee is authoritative.
            clientQuoteToken: quoteToken,
            distanceKm,
            paymentMethod,
            itemsPrice: goodsSubtotal,
            totalPrice: totalOrderCostPlusShipping,
            customerName: user?.result?.name,
            client_id: userId,
            orderSrc: orderSource,
            vendorId: currentVendor,
            idempotencyKey: idempotencyKeyRef.current,
        });

        if (orderAction.error || orderAction.meta?.requestStatus !== 'fulfilled') {
            return;
        }

        const orderId = extractCreatedOrderId(orderAction);

        // Same optional-email treatment as Airtel (see payAirtelMoneyCheckoutHandler).
        const typed = emailInput.trim();
        const effectiveEmail = userEmail || typed || undefined;
        if (!userEmail && typed) {
            apiSetContactEmail(typed).catch(() => {});
        }

        dispatch(
            payCardCheckout({
                cardDetails,
                totalPrice: totalOrderCostPlusShipping,
                client_id: userId,
                customer_name: userName,
                email: effectiveEmail,
                network: paymentMethod,
                redirect_url: `${window.location.origin}/cardOrder`,
                orderId,
            })
        )
            .then((action) => {
                if (action.payload?.redirectUrl) {
                    followCardRedirect(action.payload.redirectUrl);
                }
            })
            .catch((err) => {
                console.error('Error while dispatching payCardCheckout:', err);
            });
    };

    // Rendered inside each wired payment block below. Deliberately not
    // `required`, not validated, and absent from every disabled= expression:
    // ask, never block.
    const optionalEmailField = needsEmail ? (
        <EmailRow>
            <StyledTextField
                label={
                    <span>
                        Email <OptionalTag>optional</OptionalTag>
                    </span>
                }
                variant="outlined"
                type="email"
                color="secondary"
                value={emailInput}
                onChange={(ev) => setEmailInput(ev.target.value)}
                fullWidth
                name="contactEmail"
                placeholder="you@example.com"
                autoComplete="email"
            />
            <EmailHint>Add an email and we'll send your receipt there.</EmailHint>
        </EmailRow>
    ) : null;

    let airtelButtonText;
    switch (true) {
        case paymentCreationStatus === 'processing payment':
            airtelButtonText = 'Processing Payment...';
            break;
        case paymentCreationStatus === 'fulfilled':
            airtelButtonText = 'Payment Initiated!';
            break;
        case paymentCreationStatus === 'rejected':
            airtelButtonText = 'Payment attempt Failed, try again!';
            break;
        case orderCreationStatus === 'pending':
            airtelButtonText = 'Creating Order...';
            break;
        case orderCreationStatus === 'fulfilled':
            airtelButtonText = 'Order Created...';
            break;
        default:
            airtelButtonText = 'Proceed to Pay';
    }

    // Display helpers (presentation only)
    const contactDisplay =
        shippingAddress.customerNumber &&
        Object.keys(shippingAddress.customerNumber).length !== 0
            ? shippingAddress.customerNumber
            : 'Not available';
    const zoneDisplay =
        shippingAddress.locationName &&
        Object.keys(shippingAddress.locationName).length !== 0
            ? shippingAddress.locationName.vicinity
            : 'Not available';
    const paymentDisplay =
        paymentMethod && Object.keys(paymentMethod).length !== 0
            ? { COD: 'Cash on delivery', MOMO: 'MTN Mobile Money', Airtel: 'Airtel Money', Visa: 'Card' }[paymentMethod] || paymentMethod
            : 'Not available';
    const itemCount = products.reduce((n, p) => n + (Number(p.quantity) || 0), 0);

    // The phone row used by both MoMo and Airtel (identical logic/constraints).
    const renderMobileInput = (label, placeholder) => (
        <PhoneRow>
            <StyledTextField
                className={`${classes.input} ${postData?.mobile?.length === 10 ? classes.inputGreen : ''}`}
                id="outlined-basic"
                label={label}
                variant="outlined"
                type="number"
                color="secondary"
                value={postData?.mobile}
                onChange={(e) => {
                    if (e.target.value.length <= 10) {
                        setPostData({
                            ...postData,
                            mobile: e.target.value,
                        });
                    }
                }}
                inputProps={{
                    maxLength: 10,
                }}
                onKeyDown={(e) => {
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        e.preventDefault();
                    }
                }}
                fullWidth
                name="mobile"
                placeholder={placeholder}
                required
            />
        </PhoneRow>
    );

    return (
        <>
            <RetailerWholesalerNavbar />
            <Page>
                <Wrap>
                    <CheckoutStepper currentStep="confirm" />
                    <FreightCheckoutNotice />

                    <Heading>Review &amp; confirm</Heading>
                    <SubHeading>One last look before we place your order.</SubHeading>

                    <Columns>
                        {/* LEFT — where & what */}
                        <div>
                            <Panel>
                                <DeliverTo>
                                    <Pin>
                                        <LocationOnIcon style={{ fontSize: 18 }} />
                                    </Pin>
                                    <div style={{ minWidth: 0 }}>
                                        <DeliverWho>Deliver to · {contactDisplay}</DeliverWho>
                                        <DeliverWhere>{zoneDisplay}</DeliverWhere>
                                    </div>
                                    <PayChip>{paymentDisplay}</PayChip>
                                </DeliverTo>
                                <PanelBody>
                                    <PanelTitle>
                                        Your order · {itemCount} item{itemCount !== 1 ? 's' : ''}
                                    </PanelTitle>
                                    {products.map((item) => {
                                        const itemTotal =
                                            Number(item.price || 0) * Number(item.quantity || 0);
                                        return (
                                            <ItemRow key={item?._id}>
                                                <Thumb src={item.image} alt={item?.name} />
                                                <ItemInfo>
                                                    <ItemName>{item?.name}</ItemName>
                                                    <ItemMeta>
                                                        Qty {item?.quantity} ·{' '}
                                                        {lineHasDeal(item) && (
                                                            <StruckPrice>{formatCurrency(item.listPrice)}</StruckPrice>
                                                        )}
                                                        {formatCurrency(item.price)}
                                                    </ItemMeta>
                                                </ItemInfo>
                                                <ItemSum>{formatCurrency(itemTotal)}</ItemSum>
                                            </ItemRow>
                                        );
                                    })}
                                </PanelBody>
                            </Panel>

                            <DeliveryTimePicker controller={deliveryTime} />

                            {/* Card details live in the wide column; the pay button
                                stays in the summary so the money story reads
                                top-to-bottom. */}
                            {paymentMethod === 'Visa' && (
                                <Panel style={{ marginTop: 16 }}>
                                    <PanelBody>
                                        <PanelTitle>Card details</PanelTitle>
                                        <CreditCardForm
                                            onFormChange={(data) => {
                                                let expiry_month, expiry_year;
                                                if (typeof data.expirationDate === 'string') {
                                                    [expiry_month, expiry_year] = data.expirationDate.split('/');
                                                } else {
                                                    expiry_month = null;
                                                    expiry_year = null;
                                                }

                                                formik.setValues({
                                                    cardNumber: data.cardNumber,
                                                    expiry_month,
                                                    expiry_year,
                                                    cardCvv: data.cvv,
                                                    cardType: data.cardType,
                                                    fullName: data.fullName,
                                                });
                                            }}
                                            values={formik.values}
                                            errors={formik.errors}
                                            touched={formik.touched}
                                            handleBlur={formik.handleBlur}
                                            handleChange={formik.handleChange}
                                        />
                                    </PanelBody>
                                </Panel>
                            )}
                        </div>

                        {/* RIGHT — money, sticky, calm; the Total is the loudest element */}
                        <Summary>
                            <Panel>
                                <PanelBody>
                                    <CostRow>
                                        <span className="k">Subtotal</span>
                                        <span className="v">{formatCurrency(goodsSubtotal)}</span>
                                    </CostRow>
                                    {offerSavings > 0 && (
                                        <CostRow>
                                            <span className="k">Vendor offer</span>
                                            <VendorOfferAmount className="v">
                                                -{formatCurrency(offerSavings)}
                                            </VendorOfferAmount>
                                        </CostRow>
                                    )}
                                    <CostRow>
                                        <span className="k">Delivery</span>
                                        <span className="v" style={{ color: '#03660c', fontWeight: 600 }}>
                                            <DeliveryFeeStatus
                                                {...feeStatusProps}
                                                freightQuotedSeparately={quoteFulfillment?.required === true}
                                                value={formatCurrency(serverShippingFee || 0)}
                                            />
                                        </span>
                                    </CostRow>
                                    <Rule />
                                    <TotalRow>
                                        <span className="k">Total</span>
                                        <span className="v">
                                            <DeliveryFeeStatus
                                                {...feeStatusProps}
                                                value={formatCurrency(totalOrderCostPlusShipping)}
                                            />
                                        </span>
                                    </TotalRow>
                                    <PayLine>
                                        Paying by <b>{paymentDisplay}</b>
                                    </PayLine>

                                    {paymentMethod === 'COD' && (
                                        <>
                                            <Cta
                                                onClick={placeOrderHandler}
                                                disabled={
                                                    orderCreationStatus === 'pending' ||
                                                    !deliveryFeeReady ||
                                                    freightConsentRequired ||
                                                    belowMinimumOrder
                                                }
                                            >
                                                {orderCreationStatus === 'pending' ? (
                                                    <>
                                                        <CircularProgress
                                                            size={18}
                                                            style={{ marginRight: '8px', color: '#ffffff' }}
                                                        />
                                                        Processing...
                                                    </>
                                                ) : (
                                                    'Place order'
                                                )}
                                            </Cta>
                                            <Reassure>
                                                You won't be charged now — you pay the rider on delivery.
                                            </Reassure>
                                        </>
                                    )}

                                    {paymentMethod === 'MOMO' && (
                                        <>
                                            {renderMobileInput('Enter MTN Number', 'Enter your MTN Number...')}
                                            {optionalEmailField}
                                            <Cta
                                                onClick={payMoMoCheckoutHandler}
                                                disabled={
                                                    postData.mobile.length < 10 ||
                                                    !deliveryFeeReady ||
                                                    freightConsentRequired ||
                                                    belowMinimumOrder ||
                                                    orderCreationStatus === 'pending' ||
                                                    paymentCreationStatus === 'pending' ||
                                                    orderCreationStatus === 'fulfilled'
                                                }
                                            >
                                                <PayLogo src={momo} alt="mtn-logo" />
                                                {orderCreationStatus === 'pending' ||
                                                paymentCreationStatus === 'pending'
                                                    ? 'Processing...'
                                                    : 'Proceed to Pay'}
                                            </Cta>
                                            <Reassure>
                                                You'll get a prompt on this number to approve the payment.
                                            </Reassure>
                                        </>
                                    )}

                                    {(paymentMethod === 'Airtel' || paymentMethod === 'ZAMTEL') && (
                                        <>
                                            {renderMobileInput('Enter Airtel Number', 'Enter your Airtel Number...')}
                                            {optionalEmailField}
                                            <AirtelCta
                                                onClick={payAirtelMoneyCheckoutHandler}
                                                disabled={
                                                    postData.mobile.length < 10 ||
                                                    !deliveryFeeReady ||
                                                    freightConsentRequired ||
                                                    belowMinimumOrder ||
                                                    orderCreationStatus === 'pending' ||
                                                    paymentCreationStatus === 'pending' ||
                                                    paymentCreationStatus === 'processing payment' ||
                                                    paymentCreationStatus === 'fulfilled' ||
                                                    orderCreationStatus === 'fulfilled'
                                                }
                                            >
                                                <PayLogo src={airtel} alt="airtel-logo" />
                                                {airtelButtonText}
                                            </AirtelCta>
                                            <Reassure>
                                                You'll get a prompt on this number to approve the payment.
                                            </Reassure>
                                        </>
                                    )}

                                    {paymentMethod === 'Visa' && (
                                        <>
                                            {optionalEmailField}
                                            <Cta
                                                onClick={formik.submitForm}
                                                disabled={
                                                    paymentCreationStatus === 'pending' ||
                                                    orderCreationStatus === 'pending' ||
                                                    !deliveryFeeReady ||
                                                    freightConsentRequired ||
                                                    belowMinimumOrder ||
                                                    !formik.isValid
                                                }
                                            >
                                                {paymentCreationStatus === 'pending' ||
                                                orderCreationStatus === 'pending'
                                                    ? 'Processing...'
                                                    : 'Pay'}
                                            </Cta>
                                            <Reassure>
                                                You'll be redirected to complete the card payment securely.
                                            </Reassure>
                                        </>
                                    )}
                                </PanelBody>
                            </Panel>
                        </Summary>
                    </Columns>
                </Wrap>
            </Page>
        </>
    );
};

export default RetailerWholesalerPlaceOrderScreen;

// ── Styled Components — narrow centered column, flat surfaces, no table chrome ──
const Page = styled.div`
  background: #f4f6f6;
  min-height: 100vh;
`;

const Wrap = styled.div`
  max-width: 920px;
  margin: 0 auto;
  padding: 20px 20px 64px;
  margin-top: 70px;
  padding-top: 60px;

  @media (max-width: 768px) {
    padding-top: 165px; /* room for the mobile vendor banner */
    padding-left: 10px;
    padding-right: 10px;
  }

  @media (max-width: 480px) {
    padding-top: 60px;
    padding-bottom: 20px;
  }
`;

const Heading = styled.h1`
  font-size: 20px;
  letter-spacing: -0.01em;
  margin: 22px 0 4px;
  color: #0f2a2e;
  font-family: 'Poppins', sans-serif;
`;

const SubHeading = styled.p`
  color: #5a6b6d;
  font-size: 13.5px;
  margin: 0 0 22px;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 20px;
  align-items: start;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: #ffffff;
  border: 1px solid #e7ecec;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 42, 46, 0.06), 0 8px 24px rgba(16, 42, 46, 0.05);
  overflow: hidden;
`;

const PanelBody = styled.div`
  padding: 14px 18px 18px;
`;

const PanelTitle = styled.h2`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #5a6b6d;
  margin: 0;
  padding: 2px 0 8px;
  font-weight: 700;
`;

const DeliverTo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid #e7ecec;
`;

const Pin = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #eef7f1;
  color: #0a5c40;
  display: grid;
  place-items: center;
  flex: none;
`;

const DeliverWho = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #0f2a2e;
`;

const DeliverWhere = styled.div`
  color: #5a6b6d;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PayChip = styled.div`
  margin-left: auto;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 999px;
  background: #eef7f1;
  color: #0a5c40;
  border: 1px solid #bfe3cd;
  white-space: nowrap;
  flex: none;
`;

const ItemRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px 0;
  border-top: 1px solid #e7ecec;

  &:first-of-type {
    border-top: 0;
  }
`;

const Thumb = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 10px;
  object-fit: cover;
  background: #eee;
  flex: none;
`;

const ItemInfo = styled.div`
  min-width: 0;
`;

const ItemName = styled.div`
  font-size: 14px;
  font-weight: 550;
  color: #0f2a2e;
`;

const ItemMeta = styled.div`
  color: #5a6b6d;
  font-size: 12.5px;
`;

const ItemSum = styled.div`
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  font-size: 14px;
  color: #0f2a2e;
  white-space: nowrap;
`;

const Summary = styled.div`
  position: sticky;
  top: 90px;

  @media (max-width: 760px) {
    position: static;
  }
`;

const CostRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 14px;
  padding: 7px 0;
  color: #0f2a2e;

  .k {
    color: #5a6b6d;
  }
  .v {
    font-variant-numeric: tabular-nums;
  }
`;

const Rule = styled.div`
  height: 1px;
  background: #e7ecec;
  margin: 8px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-top: 6px;

  .k {
    font-weight: 600;
    color: #0f2a2e;
  }
  .v {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    color: #0f2a2e;
  }
`;

const PayLine = styled.div`
  font-size: 12.5px;
  color: #5a6b6d;
  margin-top: 2px;

  b {
    color: #0f2a2e;
    font-weight: 600;
  }
`;

const PhoneRow = styled.div`
  margin-top: 14px;
`;

const EmailRow = styled.div`
  margin-top: 14px;
`;

const OptionalTag = styled.span`
  color: #8a9a9c;
  font-size: 12px;
  font-weight: 500;
`;

const EmailHint = styled.div`
  font-size: 12px;
  color: #5a6b6d;
  margin-top: 6px;
`;

const Cta = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 16px;
  padding: 13px 18px;
  border: 0;
  border-radius: 12px;
  background: #03660c;
  color: #ffffff;
  font-size: 15px;
  font-weight: 650;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: #054d0c;
  }

  &:disabled {
    background: #cfd8d7;
    color: #eef2f1;
    cursor: not-allowed;
  }
`;

const AirtelCta = styled(Cta)`
  background: #e40000;

  &:hover:not(:disabled) {
    background: #b80000;
  }
`;

const PayLogo = styled.img`
  width: 40px;
  height: 30px;
  border-radius: 5px;
  object-fit: cover;
  margin-right: 10px;
`;

const Reassure = styled.div`
  text-align: center;
  font-size: 12px;
  color: #5a6b6d;
  margin-top: 10px;
`;
